/** Importação de catálogo por planilha — o painel que as abas Canais e
 *  Veículos compartilham.
 *
 *  **É o MESMO painel nas duas abas, com a MESMA lista, e isso é
 *  deliberado**: a planilha é mista (a coluna `Tipo` decide se cada
 *  linha vira canal de rede ou veículo de imprensa/TV/rádio), então
 *  filtrar o histórico por eixo esconderia metade do que a pessoa
 *  acabou de importar. A microcopy diz isso literalmente, senão a
 *  primeira reação a ver um veículo listado na aba Canais é "bug".
 *
 *  A máquina é assíncrona e sem desfazer: o arquivo vira uma execução na
 *  fila rápida, e o resultado volta como RECIBO por linha (criou /
 *  atualizou / virou apelido / recusou). Sem prévia porque prévia
 *  significaria processar duas vezes o mesmo arquivo pra depois pedir
 *  confirmação; o laço de correção é "baixar só as recusadas", corrigir
 *  no Excel e reenviar — o upsert é idempotente, então reenviar o
 *  arquivo inteiro também funciona.
 *
 *  Os contadores vêm calculados por query a cada leitura, e é por isso
 *  que o poll aqui mostra progresso de verdade em vez de um número
 *  congelado até o fim.
 *
 *  **Dois formatos (`kind`, 2026-10-10)**: `catalog` (canais e veículos,
 *  nas abas Canais e Veículos) e `publishers` (publicadores nos grupos,
 *  na aba Categorização de publicadores). É a mesma máquina — fila,
 *  recibo, recusadas, cancelar — com textos, modelo e lista próprios de
 *  cada formato: cada painel lista só as importações do SEU formato.
 *
 *  **Terceiro formato, `company`** (2026-10-10): a configuração da empresa,
 *  a planilha de várias abas que "Baixar configuração" gera. Aplica aba por
 *  aba, na ordem, cria ou atualiza por nome e NUNCA apaga. É o único que
 *  mostra o recibo POR LINHA (`CompanyImportReceipt`), porque o contador não
 *  diz em qual das abas está a recusa.
 *
 *  **Quarto formato, `rules`** (2026-10-18): o atalho da aba Premissas, num
 *  arquivo de uma aba só — mesma máquina do `company`, por isso também
 *  mostra o recibo por linha. Tem o botão extra "Baixar as premissas
 *  atuais" (a exportação da configuração recortada nessa aba).
 *
 *  **Quinto formato, `themes`** (2026-10-28): temas e subtemas, duas abas
 *  (`Temas` e `Subtemas`) aplicadas nessa ordem pela mesma máquina do
 *  `company`; também mostra o recibo por linha e tem o botão "Baixar os
 *  temas e subtemas atuais" (a exportação recortada nas duas abas).
 *
 *  **Cadastro atual nas abas Canais e Veículos** (`currentSheet`,
 *  2026-10-18): o mesmo botão no formato `catalog`, baixando a aba
 *  correspondente da configuração — o cadastro INTEIRO, desativados
 *  inclusos, com a coluna `Ativo`. */

import {
  Ban,
  Download,
  FilePlus2,
  FileSpreadsheet,
  LoaderCircle,
  Upload
} from "lucide-react";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import {
  ApiError,
  apiClient,
  type CatalogImport,
  type CatalogImportKind
} from "@/shared/api/api-client";
import { retryPauseFrom, usePolling } from "@/shared/hooks/usePolling";
import {
  importProgressPercent,
  importStatusMeta,
  isImportActive
} from "@/shared/utils/catalog-import";
import { downloadBlob } from "@/features/uploads/upload-shared";
import { CompanyImportReceipt } from "./CompanyImportReceipt";
import { configFilename } from "./ConfigExportButton";
import { FileDrop } from "@/shared/components/FileDrop";

import { errorMessage } from "@/shared/api/http";

import { formatInteger } from "@/shared/utils/format";
/** Quantas execuções passadas mostrar. O histórico completo não tem
 *  tela própria: o que a pessoa precisa aqui é a de agora e as últimas,
 *  pra baixar as recusadas de uma que acabou de rodar. */
const RECENT_IMPORTS = 5;

type Props = {
  token: string;
  /** O formato da planilha deste painel. Padrão: canais e veículos. */
  kind?: CatalogImportKind;
  /** Chamado quando uma execução TERMINA — a aba recarrega a própria
   *  lista, senão o catálogo recém-importado só apareceria no próximo
   *  clique. */
  onFinished: () => void;
  /** Nos formatos `company` e `rules`: o nome no arquivo baixado
   *  (`configuracao-<empresa>.xlsx` / `premissas-<empresa>.xlsx`). */
  companyName?: string;
  /** Só no formato `catalog`: qual aba da configuração o botão "Baixar os
   *  ... atuais" baixa (canais na aba Canais, veículos na aba Veículos).
   *  Sem ela o painel não ganha o botão. */
  currentSheet?: CurrentSheet;
  /** Dentro do pop-up de importação: o título é o do pop-up (o painel não
   *  repete o seu), e os botões vêm PRIMEIRO, com as importações recentes
   *  logo abaixo e as instruções no fim (2026-10-20, decisão da dona: a
   *  pessoa acha o que fazer sem rolar a tabela de colunas). */
  inDialog?: boolean;
  /** O guia das colunas da planilha, mostrado logo depois da explicação
   *  geral (o pop-up de importação o monta por formato). */
  guide?: ReactNode;
  /** Avisa quem abriu o painel se há importação andando: o botão que
   *  abre o pop-up mostra "Importando…" com ele fechado. */
  onActiveChange?: (active: boolean) => void;
};

export type CurrentSheet = "channels" | "vehicles";

/** O que cada aba baixa: o botão, o prefixo do arquivo e a falha. */
const CURRENT_SHEET: Record<
  CurrentSheet,
  { label: string; prefix: string; failure: string }
> = {
  channels: {
    label: "Baixar os canais atuais",
    prefix: "canais",
    failure: "Não foi possível baixar os canais."
  },
  vehicles: {
    label: "Baixar os veículos atuais",
    prefix: "veiculos",
    failure: "Não foi possível baixar os veículos."
  }
};

/** A ordem em que a importação da empresa aplica as abas da planilha —
 *  a mesma do servidor; a tela só a mostra. */
const COMPANY_SHEET_ORDER = [
  "Empresa",
  "Marcas",
  "Concorrentes",
  "Canais",
  "Veículos",
  "Porte do publicador",
  "Grupos de publicador",
  "Publicadores",
  "Temas",
  "Subtemas",
  "Porta-vozes",
  "Mensagens-chave",
  "Citação no título",
  "Foco",
  "Termos de classificação",
  "Premissas",
  "Textos de prompt"
];

function ImportCounters({ run }: { run: CatalogImport }) {
  const cells: [string, number][] = [
    ["criados", run.created_count],
    ["atualizados", run.updated_count],
    ["apelidos", run.alias_created_count],
    ["recusados", run.rejected_count]
  ];
  // "Pulados" só existe na configuração da empresa (abas que o formato
  // ainda não importa) — nos outros dois seria um zero eterno.
  if (run.kind === "company") cells.push(["pulados", run.skipped_count]);
  return (
    <dl className="import-counters">
      {cells.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{formatInteger(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

const TEMPLATE_FILENAME: Record<CatalogImportKind, string> = {
  catalog: "modelo-catalogo.xlsx",
  publishers: "modelo-publicadores.xlsx",
  company: "modelo-configuracao.xlsx",
  rules: "modelo-premissas.xlsx",
  themes: "modelo-temas.xlsx"
};

const PANEL_TITLE: Record<CatalogImportKind, string> = {
  catalog: "Importar por planilha",
  publishers: "Importar publicadores por planilha",
  company: "Importar configuração por planilha",
  rules: "Importar premissas por planilha",
  themes: "Importar temas e subtemas por planilha"
};

/** Os formatos cujas linhas carregam a chave da aba: só neles o recibo é
 *  POR LINHA (`CompanyImportReceipt`) — o contador não diz em qual aba
 *  está a recusa. */
const RECEIPT_KINDS: CatalogImportKind[] = ["company", "rules", "themes"];

/** O texto explicativo de cada formato. No de catálogo, o parágrafo é o
 *  de sempre; no de publicadores ele diz as quatro colunas e as regras
 *  de recusa (a planilha põe pessoas EM grupos, não cria grupos). O da
 *  configuração da empresa virou os três passos do painel (2026-10-14). */
function ImportHint({
  kind,
  currentSheet
}: {
  kind: CatalogImportKind;
  currentSheet?: CurrentSheet;
}) {
  if (kind === "publishers") {
    return (
      <p className="section-hint">
        Uma linha por <strong>perfil</strong>, com quatro colunas:{" "}
        <strong>Nome</strong>, <strong>Plataforma</strong> (um canal de rede
        cadastrado), <strong>URL do perfil</strong> e <strong>Grupos</strong>{" "}
        (nomes separados por <strong>;</strong>). Linhas com o mesmo nome são a
        mesma pessoa: cada uma acrescenta o seu perfil e os seus grupos. Plataforma e
        URL podem ficar vazias — a linha só põe a pessoa nos grupos. A
        planilha <strong>não cria grupos</strong>: o grupo precisa existir
        (da empresa ou global) e é cadastrado em <strong>Novo grupo</strong>, com o peso. Também é
        recusada a linha cuja URL de perfil já é de outra pessoa; o resto
        entra, e as recusadas saem numa planilha com o motivo.
      </p>
    );
  }
  if (kind === "themes") {
    return (
      <p className="section-hint">
        Duas abas: <strong>Temas</strong> e <strong>Subtemas</strong>, nesta ordem
        — um subtema só entra se o tema dele já existe (ativo) ou vem na aba
        Temas do mesmo arquivo, que é aplicada antes. <strong>Nada é apagado</strong>:
        nome que já existe é atualizado, nome novo é criado, e o que está
        cadastrado e não está na planilha fica como está.{" "}
        <strong>Ativo = NÃO</strong> desativa. A coluna <strong>Concorrente</strong>{" "}
        decide de quem é a linha (vazia é o cliente). O arquivo baixado em{" "}
        <strong>Baixar os temas e subtemas atuais</strong> volta por aqui, e a
        planilha de antes da troca de nomes (abas “Grupos de tema” e “Temas”) também é
        aceita. Para trazer várias abas de uma vez, use Importar configuração.
      </p>
    );
  }
  if (kind === "rules") {
    return (
      <p className="section-hint">
        Uma linha por <strong>premissa</strong>, com as oito colunas da aba
        Premissas: <strong>Concorrente</strong>, <strong>Marca</strong>,{" "}
        <strong>Tarefa</strong>, <strong>Fonte</strong>,{" "}
        <strong>Tipo de regra</strong>, <strong>Posição</strong>,{" "}
        <strong>Texto</strong> e <strong>Ativo</strong>. A coluna Concorrente
        decide de quem é a linha — vazia é o cliente; o seletor “Cadastro
        de” acima não muda o painel. <strong>Nada é apagado</strong>:{" "}
        <strong>Ativo = NÃO</strong> desativa; premissa que já existe na
        posição é atualizada e a nova entra na próxima posição livre da seção.
        Linha recusada sai na planilha de recusadas com o motivo. Para trazer
        várias abas de uma vez, use Importar configuração.
      </p>
    );
  }
  return (
    <p className="section-hint">
      Uma planilha só para os dois cadastros — a coluna <strong>Tipo</strong>{" "}
      decide o que cada linha vira e aceita{" "}
      <strong>rede, online, impresso, tv ou radio</strong>: rede é canal de
      rede social; online e impresso são os dois jeitos de dizer imprensa
      (Impresso e On-line); tv e radio são TV e Rádio. Por isso <strong>a lista de importações é a mesma nas duas
      abas</strong>: filtrar por aba esconderia metade do arquivo que você
      acabou de mandar. Linha incompleta (veículo sem tier, rede sem peso) é
      recusada e sai na planilha de recusadas — o resto entra.
      {currentSheet && (
        <>
          {" "}
          O arquivo baixado em <strong>Baixar os{" "}
          {currentSheet === "channels" ? "canais" : "veículos"} atuais</strong>{" "}
          é o cadastro inteiro, com a coluna <strong>Ativo</strong> e os
          desativados incluídos; se você reenviá-lo, a linha de um desativado
          é recusada mandando reativar na aba.
        </>
      )}
    </p>
  );
}

export function CatalogImportPanel({
  token,
  kind = "catalog",
  onFinished,
  companyName,
  currentSheet,
  inDialog = false,
  guide,
  onActiveChange
}: Props) {
  const [runs, setRuns] = useState<CatalogImport[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  // Até quando o servidor pediu pra esperar (429), lido no `catch` da
  // carga; o `usePolling` abaixo respeita (2026-10-25).
  const [pausedUntil, setPausedUntil] = useState<number | null>(null);
  const [busy, setBusy] = useState<null | "template" | "current" | "submit">(null);
  const showsReceipt = RECEIPT_KINDS.includes(kind);
  // Decidido uma vez: qual recorte da exportação o botão "atuais" baixa.
  const currentDownload: {
    sheets?: string[];
    prefix?: string;
    label?: string;
    failure: string;
  } =
    kind === "rules"
      ? {
          sheets: ["rules"],
          prefix: "premissas",
          label: "Baixar as premissas atuais",
          failure: "Não foi possível baixar as premissas."
        }
      : kind === "themes"
        ? {
            sheets: ["theme_groups", "themes"],
            prefix: "temas",
            label: "Baixar os temas e subtemas atuais",
            failure: "Não foi possível baixar os temas e subtemas."
          }
      : kind === "catalog" && currentSheet
        ? { sheets: [currentSheet], ...CURRENT_SHEET[currentSheet] }
        : { failure: "Não foi possível baixar a configuração." };
  const [rowBusy, setRowBusy] = useState<number | null>(null);
  // O recibo por linha (só na configuração da empresa): qual execução
  // está aberta.
  const [receiptOf, setReceiptOf] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const page = await apiClient.listCatalogImports(token, {
        size: RECENT_IMPORTS,
        kind
      });
      setRuns(page.items);
      // Limpa o aviso quando a carga volta a funcionar — falha
      // transiente não pode ficar na cara ao lado da lista já
      // carregada.
      //
      // Isto nasceu como remendo de uma corrida que não existe mais: o
      // shell publicava a empresa ativa num efeito, e efeito de filho
      // roda antes do efeito do pai, então a primeira requisição saía
      // sem `X-Company-Id` e tomava 401. Desde 2026-08-30 a publicação
      // acontece no RENDER do shell (`AppShellProvider`), e o valor já
      // está lá quando este painel monta. A limpeza fica porque
      // continua certa para o caso geral.
      setFailure(null);
    } catch (error) {
      // Um 429 é pedido pra esperar: o polling da execução ativa
      // respeita o `Retry-After` em vez de bater no próximo tick.
      setPausedUntil(retryPauseFrom(error));
      setFailure(
        error instanceof ApiError
          ? error.message
          : "Não foi possível carregar as importações."
      );
    }
  }, [token, kind]);

  useEffect(() => {
    void load();
  }, [load]);

  const active = runs.find(isImportActive) ?? null;

  /** Avisa a aba quando a execução ativa some. O id anterior fica numa
   *  ref porque o efeito precisa comparar com o render passado — sem
   *  isso, ou ele dispara a cada poll, ou nunca. */
  const previousActiveId = useRef<number | null>(null);
  useEffect(() => {
    if (previousActiveId.current !== null && active === null) onFinished();
    previousActiveId.current = active?.id ?? null;
  }, [active, onFinished]);

  const isActive = active !== null;
  useEffect(() => {
    onActiveChange?.(isActive);
  }, [isActive, onActiveChange]);

  // A assinatura inclui os contadores porque eles são a única coisa que
  // muda enquanto o status fica em `STARTED` — sem eles o backoff do
  // hook trataria uma importação em pleno andamento como "parada".
  usePolling({
    enabled: active !== null,
    onTick: () => void load(),
    pausedUntil,
    signature: [
      active?.id ?? "",
      active?.status ?? "",
      active?.pending_count ?? "",
      active?.rejected_count ?? ""
    ].join(":")
  });

  const downloadTemplate = async () => {
    setBusy("template");
    setFailure(null);
    try {
      downloadBlob(
        await apiClient.downloadCatalogTemplate(token, kind),
        TEMPLATE_FILENAME[kind]
      );
    } catch (error) {
      setFailure(
        errorMessage(error, "Não foi possível baixar o modelo.")
      );
    } finally {
      setBusy(null);
    }
  };

  /** "Baixar configuração atual" — o mesmo arquivo do botão do menu, ao
   *  lado de onde ele vai ser reenviado. No atalho de premissas é a mesma
   *  exportação recortada na aba Premissas. */
  const downloadCurrent = async () => {
    setBusy("current");
    setFailure(null);
    const { sheets, prefix, failure } = currentDownload;
    try {
      downloadBlob(
        await apiClient.downloadCompanyConfig(
          token,
          undefined,
          sheets ? { sheets } : undefined
        ),
        configFilename(companyName, prefix)
      );
    } catch (error) {
      setFailure(errorMessage(error, failure));
    } finally {
      setBusy(null);
    }
  };

  const submit = async (file: File) => {
    setBusy("submit");
    setFailure(null);
    try {
      await apiClient.importCatalog(token, file, kind);
      await load();
    } catch (error) {
      // 409 é a trava de execução: uma importação por empresa de cada
      // vez, senão duas planilhas disputariam o mesmo cadastro. A
      // mensagem do servidor já nomeia a que está rodando.
      setFailure(
        error instanceof ApiError
          ? error.message
          : "Não foi possível enviar a planilha."
      );
    } finally {
      setBusy(null);
    }
  };

  const cancel = async (importId: number) => {
    setRowBusy(importId);
    setFailure(null);
    try {
      await apiClient.cancelCatalogImport(token, importId);
      await load();
    } catch (error) {
      setFailure(
        error instanceof ApiError
          ? error.message
          : "Não foi possível cancelar a importação."
      );
    } finally {
      setRowBusy(null);
    }
  };

  const downloadRejected = async (importId: number) => {
    setRowBusy(importId);
    setFailure(null);
    try {
      downloadBlob(
        await apiClient.downloadCatalogRejected(token, importId),
        `recusadas-import-${importId}.xlsx`
      );
    } catch (error) {
      setFailure(
        error instanceof Error
          ? error.message
          : "Não foi possível baixar as recusadas."
      );
    } finally {
      setRowBusy(null);
    }
  };

  return (
    <div className={`panel-section panel-import${showsReceipt ? " company-import" : ""}`}>
      {!inDialog && <h3>{PANEL_TITLE[kind]}</h3>}

      {kind === "company" ? (
        <ol className="import-steps">
          <li>
            <span className="admin-step-num" aria-hidden="true">1</span>
            <div className="import-step-body">
              <h4>Baixe a planilha</h4>
              <p className="section-hint">
                A configuração atual desta empresa já vem preenchida — o jeito mais rápido
                de mudar só o que precisa. Para montar do zero, use o modelo em branco.
              </p>
              <div className="edit-form-actions">
                <button
                  type="button"
                  className="secondary-button"
                  disabled={busy !== null}
                  onClick={() => void downloadCurrent()}
                >
                  {busy === "current" ? <LoaderCircle className="spin" size={16} /> : <Download size={16} />}
                  Baixar configuração atual
                </button>
                <button
                  type="button"
                  className="secondary-button"
                  disabled={busy !== null}
                  onClick={() => void downloadTemplate()}
                >
                  {busy === "template" ? <LoaderCircle className="spin" size={16} /> : <FilePlus2 size={16} />}
                  Baixar modelo
                </button>
              </div>
            </div>
          </li>
          <li>
            <span className="admin-step-num" aria-hidden="true">2</span>
            <div className="import-step-body">
              <h4>Edite no Excel</h4>
              <ul className="import-rules">
                <li>
                  <strong>Cria ou atualiza por nome.</strong> O que está no sistema e não está
                  na planilha <strong>nunca é apagado</strong>.
                </li>
                <li>
                  <strong>Ativo = NÃO</strong> desativa o item (ou o cria já desativado);{" "}
                  <strong>Ativo = SIM</strong> reativa.
                </li>
                <li>
                  Linha que cita uma marca, um grupo ou um concorrente que não existe é
                  recusada, dizendo qual aba falta.
                </li>
                <li>
                  Em <strong>Citação no título</strong> cada linha é um termo da categoria (sem
                  repetir); em <strong>Textos de prompt</strong> a planilha só atualiza as
                  linhas que a empresa já tem.
                </li>
              </ul>
              <details className="import-order">
                <summary>Ordem em que as abas são aplicadas</summary>
                <ol>
                  {COMPANY_SHEET_ORDER.map(sheet => (
                    <li key={sheet}>{sheet}</li>
                  ))}
                </ol>
              </details>
            </div>
          </li>
          <li>
            <span className="admin-step-num" aria-hidden="true">3</span>
            <div className="import-step-body">
              <h4>Envie a planilha</h4>
              <FileDrop
                accept=".xlsx"
                files={[]}
                onChange={files => {
                  if (files[0]) void submit(files[0]);
                }}
                disabled={busy !== null || active !== null}
                formats={busy === "submit" ? "Enviando…" : "Arquivo .xlsx — a importação começa assim que o arquivo chega"}
                help={
                  active
                    ? `Espere "${active.filename}" terminar — uma importação por vez.`
                    : "Acompanhe o andamento e o recibo logo abaixo."
                }
              />
            </div>
          </li>
        </ol>
      ) : (
        !inDialog && (
          <>
            <ImportHint kind={kind} currentSheet={currentSheet} />
            {guide}
          </>
        )
      )}

      {failure && <div className="login-error">{failure}</div>}

      {kind !== "company" && (
      <div className="edit-form-actions">
        <button
          type="button"
          className="secondary-button"
          disabled={busy !== null}
          onClick={() => void downloadTemplate()}
        >
          {busy === "template" ? (
            <LoaderCircle className="spin" size={16} />
          ) : (
            <Download size={16} />
          )}
          Baixar modelo
        </button>
        {currentDownload.label && (
          <button
            type="button"
            className="secondary-button"
            disabled={busy !== null}
            onClick={() => void downloadCurrent()}
          >
            {busy === "current" ? (
              <LoaderCircle className="spin" size={16} />
            ) : (
              <Download size={16} />
            )}
            {currentDownload.label}
          </button>
        )}
        <button
          type="button"
          className="secondary-button"
          disabled={busy !== null || active !== null}
          title={
            active
              ? `Espere "${active.filename}" terminar — uma importação por vez`
              : undefined
          }
          onClick={() => inputRef.current?.click()}
        >
          {busy === "submit" ? (
            <LoaderCircle className="spin" size={16} />
          ) : (
            <Upload size={16} />
          )}
          Importar planilha
        </button>
        {/* Input escondido acionado pelo botão: o controle nativo não
            aceita estilo e é o único elemento da tela que ignoraria o
            design system. `value = ""` no fim permite reenviar o MESMO
            arquivo depois de corrigi-lo — sem isso o `change` não
            dispara na segunda vez. */}
        <input
          ref={inputRef}
          type="file"
          hidden
          accept=".csv,.xlsx,.xls"
          onChange={event => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file) void submit(file);
          }}
        />
      </div>
      )}

      {runs.length > 0 && (showsReceipt || inDialog) && (
        <h4 className="import-history-title">Importações recentes</h4>
      )}
      {runs.length === 0 ? (
        <p className="section-hint">Nenhuma importação registrada ainda.</p>
      ) : (
        <div className="list-cards-grid">
          {runs.map(run => {
            const meta = importStatusMeta(run.status);
            const running = isImportActive(run);
            return (
              <div key={run.id} className="item-card import-card">
                <div className="item-card-info">
                  <FileSpreadsheet size={20} className="item-card-icon" />
                  <div className="import-card-body">
                    <strong>{run.filename}</strong>
                    <span>
                      #{run.id} · {formatInteger(run.total_rows)}{" "}
                      {run.total_rows === 1 ? "linha" : "linhas"}
                      {run.message ? ` · ${run.message}` : ""}
                    </span>
                    <ImportCounters run={run} />
                    {running && (
                      <span className="import-progress-track">
                        <span
                          className="import-progress-fill"
                          style={{ width: `${importProgressPercent(run)}%` }}
                        />
                      </span>
                    )}
                  </div>
                </div>
                <div className="item-card-actions">
                  <span className={`badge-pill ${meta.tone}`}>{meta.label}</span>
                  {showsReceipt && run.total_rows > 0 && (
                    <button
                      type="button"
                      className="text-button"
                      aria-expanded={receiptOf === run.id}
                      onClick={() =>
                        setReceiptOf(current => (current === run.id ? null : run.id))
                      }
                    >
                      {receiptOf === run.id ? "Ocultar recibo" : "Ver recibo por linha"}
                    </button>
                  )}
                  {run.rejected_count > 0 && (
                    <button
                      type="button"
                      className="text-button"
                      disabled={rowBusy === run.id}
                      onClick={() => void downloadRejected(run.id)}
                    >
                      Baixar recusadas
                    </button>
                  )}
                  {running && (
                    <button
                      type="button"
                      className="text-button"
                      disabled={rowBusy === run.id}
                      title="Libera a trava. O que já foi aplicado FICA; reimportar o mesmo arquivo completa o resto."
                      onClick={() => void cancel(run.id)}
                    >
                      {rowBusy === run.id ? (
                        <LoaderCircle className="spin" size={15} />
                      ) : (
                        <Ban size={15} />
                      )}
                      Cancelar
                    </button>
                  )}
                </div>
                {showsReceipt && receiptOf === run.id && (
                  <CompanyImportReceipt token={token} importId={run.id} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {inDialog && kind !== "company" && (
        <div className="import-instructions">
          <h4 className="import-history-title">Como preencher a planilha</h4>
          <ImportHint kind={kind} currentSheet={currentSheet} />
          {guide}
        </div>
      )}
    </div>
  );
}
